// Bugsnag error reporting + performance, bundled — replaces the CDN <script> tags and the
// inline Bugsnag.start() that lived in BIZ's twig-views/base.twig.
//
// A page opts in with two meta tags, placed BEFORE the bundle's <script>:
//   <meta name="bugsnag-api-key" content="…">
//   <meta name="bugsnag-release-stage" content="production">
// No api-key meta → nothing starts, so a page (or another BSEE consumer) that doesn't report
// pays only the bundle bytes. Starting twice is guarded: one start per page, whatever calls this.

import Bugsnag, { type Event } from '@bugsnag/js';
import BugsnagPerformance from '@bugsnag/browser-performance';

let started = false;

function metaContent(name: string): string {
    const tag = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
    if (tag === null) {
        return '';
    }
    return tag.content.trim();
}

/**
 * Errors that are not ours to fix, dropped before they report. Return false to drop.
 *
 * HelpScout Beacon (ACP help + chat) lazy-loads its UI in chunks. Leaving the page while one is
 * still loading aborts it and Beacon throws ChunkLoadError from its own code — nothing breaks for
 * the user. Drop just that; every other Beacon error still reports.
 */
export function bugsnagDropNoise(event: Event): boolean | void {
    const error = event.errors[0];
    if (error && error.errorClass === 'ChunkLoadError' && /helpscout\.net/.test(error.errorMessage)) {
        return false;
    }
}

/**
 * Start Bugsnag (errors, then performance) from the page's meta tags. Called by the bundle entry
 * at load — explicitly, because a lib build drops a module's side-effect body.
 */
export function startBugsnag(): void {
    if (started) {
        return;
    }
    const apiKey = metaContent('bugsnag-api-key');
    if (apiKey === '') {
        return;
    }
    started = true;

    const releaseStage = metaContent('bugsnag-release-stage') || 'production';

    Bugsnag.start({ apiKey, releaseStage, onError: bugsnagDropNoise });
    BugsnagPerformance.start({ apiKey, releaseStage });

    // Page scripts call the bare global — e.g. ca/customer.js's Bugsnag.notify() tripwire.
    (window as any).Bugsnag = Bugsnag;
}

export { Bugsnag };
